import { API_URL } from './config';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function send(
  path: string,
  params: any,
  retryCount: number = 0
): Promise<any> {
  const reqParams = Object.assign(
    {},
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    params
  );

  try {
    const response = await fetch(API_URL + path, reqParams);

    if (response.status === 204) {
      return null;
    }

    // Handle rate limiting (429) with retry logic
    if (response.status === 429) {
      const maxRetries = 5;

      if (retryCount >= maxRetries) {
        throw new Error(
          `Rate limit exceeded. Max retries (${maxRetries}) reached.`
        );
      }

      // Get retry delay from Retry-After header (in seconds) or default to exponential backoff
      const retryAfterHeader = response.headers.get('Retry-After');
      let retryDelayMs: number;

      if (retryAfterHeader) {
        // Retry-After can be in seconds or HTTP date format
        const retryAfterSeconds = parseInt(retryAfterHeader, 10);
        if (!isNaN(retryAfterSeconds)) {
          retryDelayMs = retryAfterSeconds * 1000;
        } else {
          // If it's a date, calculate the difference
          const retryAfterDate = new Date(retryAfterHeader);
          retryDelayMs = Math.max(0, retryAfterDate.getTime() - Date.now());
        }
      } else {
        // Exponential backoff if no Retry-After header: 1s, 2s, 4s, 8s, 16s
        retryDelayMs = Math.pow(2, retryCount) * 1000;
      }

      // Cap the delay at 30 seconds to avoid extremely long waits
      retryDelayMs = Math.min(retryDelayMs, 30000);

      console.log(
        `Rate limited (429). Retrying in ${retryDelayMs}ms... (attempt ${
          retryCount + 1
        }/${maxRetries + 1})`
      );

      await sleep(retryDelayMs);
      return send(path, params, retryCount + 1);
    }

    if (!response.ok) {
      // Check content type before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const errResponse = await response.json();
          if (errResponse && errResponse.message) {
            throw new Error(errResponse.message);
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (parseError) {
          // If JSON parsing fails, throw a more helpful error
          throw new Error(`Request failed with status ${response.status}. Server returned non-JSON response.`);
        }
      } else {
        // Server returned HTML or other non-JSON content
        const statusText = response.statusText || 'Unknown error';
        throw new Error(`Request failed with status ${response.status} (${statusText}). The endpoint may not exist or there was a server error.`);
      }
    }

    return response.json();
  } catch (error) {
    // If it's a network error and we haven't exceeded retries, try again
    if (
      retryCount < 2 &&
      (error instanceof TypeError ||
        (error instanceof Error && error.message.includes('fetch')))
    ) {
      console.log(`Network error. Retrying... (attempt ${retryCount + 1}/3)`);
      await sleep(1000 * (retryCount + 1)); // 1s, 2s delay for network errors
      return send(path, params, retryCount + 1);
    }

    throw error;
  }
}

export function post(path: string, data: any) {
  return send(path, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function patch(path: string, data: any) {
  return send(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function del(path: string) {
  return send(path, {
    method: 'DELETE',
  });
}

export function get(path: string, headers?: any) {
  return send(path, {
    method: 'GET',
    headers,
  });
}

export function getExternal(url: string, headers?: any) {
  return fetch(url)
    .then((res) => {
      return res.json();
    })
    .then((data) => {
      return data;
    })
    .catch((err) => {
      console.log('err', err);
    });
}

export function fetchUser() {
  return get('/users/me');
}

export function destroySession() {
  return del('/sessions');
}
